import React, { useEffect, useState } from "react";
import { Star, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../misc/Dialog";
import { Button } from "../../misc/Button";
import { cn } from "../../../utils/classMerger";
import { Images } from "../../../types/listingImages";
import { orderListingImagesByTag } from "../slideshow/reorderByTag";

interface ChangePrimaryImageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  images: Images[];
  mode: "change" | "exclude";
  // Image locked out of selection: the current primary (change) or the
  // image being excluded (exclude). A new primary must be a different image.
  lockedImageId?: string;
  onConfirm: (newPrimaryId: string) => void;
  isSaving: boolean;
}

const ChangePrimaryImageDialog: React.FC<ChangePrimaryImageDialogProps> = ({
  open,
  onOpenChange,
  images,
  mode,
  lockedImageId,
  onConfirm,
  isSaving,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedId(null);
    }
  }, [open]);

  // Excluded images can't be a primary, so they're hidden entirely. Remaining
  // images follow the slideshow's natural tour order (exterior → living → …).
  const displayedImages = orderListingImagesByTag(
    images.filter((image) => !image.excludeFromPosts),
  );

  const isSelectable = (image: Images): boolean => image.id !== lockedImageId;

  const title =
    mode === "exclude" ? "Pick a new primary image" : "Change primary image";
  const description =
    mode === "exclude"
      ? "The primary image is used on every post, so it can't be excluded directly. Choose a new primary - the current one will then be excluded from posts."
      : "Choose which image is used first on every post and as the image for sold posts.";
  const confirmLabel =
    mode === "exclude"
      ? "Set new primary & exclude this image"
      : "Set as primary";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="relative max-w-3xl bg-white">
        <DialogClose
          className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none"
          disabled={isSaving}
        >
          <X className="h-5 w-5 text-primary" />
          <span className="sr-only">Close</span>
        </DialogClose>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="mt-4 grid grid-cols-2 min-[640px]:grid-cols-3 sxl:grid-cols-4 gap-3">
          {displayedImages.map((image) => {
            const selectable = isSelectable(image);
            const isSelected = selectedId === image.id;
            return (
              <button
                key={image.id}
                type="button"
                disabled={!selectable || isSaving}
                onClick={() => setSelectedId(image.id)}
                className={cn(
                  "relative aspect-square overflow-hidden rounded-lg border-2 transition-all",
                  isSelected
                    ? "border-secondary ring-2 ring-secondary"
                    : "border-transparent",
                  !selectable && "opacity-50 cursor-not-allowed",
                )}
              >
                <img
                  src={image.signedUrl}
                  alt={image.tag || "Listing image"}
                  className="h-full w-full object-cover"
                />
                {image.id === lockedImageId && (
                  <span className="absolute top-1 left-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-primary">
                    Current
                  </span>
                )}
                {isSelected && (
                  <span className="absolute bottom-1 right-1 flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-primary">
                    <Star className="h-3 w-3 fill-primary" />
                    Primary
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={() => selectedId && onConfirm(selectedId)}
            disabled={!selectedId || isSaving}
          >
            {isSaving ? "Saving..." : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePrimaryImageDialog;
